import React, { useEffect, useState, useRef } from "react";
import { useHref, useNavigate, useParams } from "react-router-dom";
import {
    FaArrowLeft,
    FaCircleInfo,
    FaNetworkWired,
    FaMicrochip,
    FaWifi,
    FaPenToSquare,
    FaTrashCan,
    FaPowerOff,
    FaTag,
    FaServer,
    FaHashtag,
    FaSignal,
    FaLaptop,
    FaMobileScreen,
    FaEye,
    FaEyeSlash,
    FaGlobe,
    FaChevronDown,
    FaLock,
} from "react-icons/fa6";
import { FaHistory, FaNetworkWired as FaNetIcon } from "react-icons/fa";
import { IoMdRefresh } from "react-icons/io";
import { useHookGenieAcsById } from "../../../../hooks/useHookGebieAcsById";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { GenieAcsAddDeviceTag } from "./modals/add_tag";
import { GenieAcsService } from "../../../../services/GenieAcsService";

// ============================================
// FIXED PPPoE DEFAULTS (tidak bisa diubah)
// ============================================
const PPPoE_FIXED = {
    ip_version: "IPv4",
    mode: "PPPoE",
    connection_name: "INTERNET",
    dhcp_enable: true,
    nat_enable: true,
    business_model: "INTERNET",
    vlan_mode: "Rewrite (tag)",
    dscp: "",
    p_802_1p: "0",
    mtu: "1480",
    enable_dscp: false,
};

// ============================================
// SUB COMPONENT: Timeline Log
// ============================================
const LogItem = ({ type, message, time }) => {
    const getIcon = () => {
        if (type === "inform") return <FaCircleInfo className="text-blue-400" />;
        if (type === "reboot") return <FaPowerOff className="text-amber-400" />;
        if (type === "boot") return <IoMdRefresh className="text-green-400" />;
        return <FaHistory className="text-slate-500" />;
    };

    return (
        <div className="relative pl-6 pb-6 border-l border-slate-700/50 last:border-0 last:pb-0">
            <div className="absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full bg-slate-900 border border-slate-500" />
            <div className="flex items-center gap-2 mb-1">
                {getIcon()}
                <span className="text-xs font-semibold text-slate-300 uppercase">{type}</span>
                <span className="text-[10px] text-slate-500 ml-auto">{time}</span>
            </div>
            <p className="text-xs text-slate-400">{message}</p>
        </div>
    );
};

// ============================================
// SUB COMPONENT: Checkbox kecil
// ============================================
const CheckboxSmall = ({ label, checked, onChange, disabled = false }) => (
    <label className={`flex items-center gap-2 select-none group ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
        <div
            className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                checked
                    ? 'bg-cyan-500 border-cyan-500'
                    : 'border-slate-600 bg-slate-800 group-hover:border-slate-500'
            }`}
        >
            {checked && <span className="text-[10px] text-white font-bold">✓</span>}
        </div>
        <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            className="hidden"
        />
        <span className="text-xs text-slate-400 group-hover:text-slate-300">{label}</span>
    </label>
);

// ============================================
// SUB COMPONENT: Fixed Config Chip (info saja)
// ============================================
const ConfigChip = ({ label, value }) => (
    <div className="flex items-center gap-2 bg-slate-800/60 rounded-lg px-3 py-2 border border-slate-700/40">
        <span className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide whitespace-nowrap">{label}</span>
        <span className="text-xs text-slate-300 font-mono font-medium">{value}</span>
    </div>
);

// ============================================
// SUB COMPONENT: SSID Multi-Select Dropdown
// ============================================
const SsidMultiSelect = ({ wifiForm, selectedSsids, onToggle, disabled }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const isSelected = (idx) => selectedSsids.includes(idx);
    const selectedLabels = wifiForm
        .filter((_, idx) => isSelected(idx))
        .map((w) => w.ssid || `SSID #${w.index}`);

    return (
        <div className="relative" ref={ref}>
            <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1.5 block">
                SSID Selection
            </label>
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen(!open)}
                className={`w-full flex items-center justify-between bg-slate-800 border text-sm rounded-lg px-3 py-2.5 transition-all ${
                    disabled
                        ? 'border-slate-700/40 text-slate-600 cursor-not-allowed'
                        : open
                        ? 'border-cyan-500/50 text-slate-200'
                        : 'border-slate-700 text-slate-200 hover:border-slate-500'
                }`}
            >
                <span className="truncate">
                    {selectedLabels.length > 0
                        ? selectedLabels.join(", ")
                        : <span className="text-slate-500">Pilih SSID...</span>}
                </span>
                <FaChevronDown
                    className={`ml-2 text-slate-500 text-xs transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {open && (
                <div className="absolute z-20 mt-1 w-full bg-slate-800 border border-slate-700 rounded-xl shadow-xl shadow-black/40 overflow-hidden">
                    {wifiForm.length === 0 ? (
                        <div className="px-4 py-3 text-xs text-slate-500 italic">
                            Tidak ada SSID tersedia
                        </div>
                    ) : (
                        wifiForm.map((wifi, idx) => {
                            const checked = isSelected(idx);
                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => onToggle(idx)}
                                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-xs transition-colors ${
                                        checked
                                            ? 'bg-cyan-500/10 text-cyan-300'
                                            : 'text-slate-300 hover:bg-slate-700/50'
                                    }`}
                                >
                                    <div
                                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all ${
                                            checked
                                                ? 'bg-cyan-500 border-cyan-500'
                                                : 'border-slate-600 bg-slate-900'
                                        }`}
                                    >
                                        {checked && <span className="text-[10px] text-white font-bold">✓</span>}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <span className="font-medium truncate block">
                                            {wifi.ssid || `SSID #${wifi.index}`}
                                        </span>
                                        <span className={`text-[10px] ${wifi.enable ? 'text-emerald-400' : 'text-slate-500'}`}>
                                            {wifi.enable ? 'Active' : 'Disabled'}
                                        </span>
                                    </div>
                                    <FaWifi className={`text-xs flex-shrink-0 ${checked ? 'text-cyan-400' : 'text-slate-600'}`} />
                                </button>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================
// MAIN COMPONENT: GenieAcsDetail
// ============================================
export const GenieAcsDetail = () => {
    const { id } = useParams();

    const { data, loading, error, refreshData } = useHookGenieAcsById({ deviceId: id });

    const navigate = useNavigate();
    const [showSidebar, setShowSidebar] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showAddTagModal, setShowAddTagModal] = useState(false);

    // 1. STATE UNTUK FORM WI-FI NETWORKS
    const [wifiForm, setWifiForm] = useState([]);
    const [idModem, setIdModem] = useState(null);
    const [loadingUpdateWifi, setLoadingUpdateWifi] = useState(false);

    // 2. STATE UNTUK FORM PPPOE (hanya field yang bisa di-edit)
    const [pppoeForm, setPppoeForm] = useState({
        username: "",
        password: "",
        enable_vlan: false,
        vlan_id: "",
        port_binding: {
            lan1: true,
            lan2: true,
            lan3: true,
            lan4: true,
        },
        selected_ssids: [], // array of index wifiForm
    });
    const [loadingUpdatePppoe, setLoadingUpdatePppoe] = useState(false);

    // Synchronize data API ke local state form
    useEffect(() => {
        if (data) {
            if (data?.wifi_networks) {
                setIdModem(data.id_modem);
                setWifiForm(
                    data.wifi_networks.map((item, index) => ({
                        index: item.index ?? index + 1,
                        ssid: item.ssid || "",
                        password: item.password || "",
                        enable: item.enable ?? false,
                    }))
                );
            }
            // Sinkronisasi data PPPoE jika ada dari API
            if (data?.pppoe_config) {
                const cfg = data.pppoe_config;
                setPppoeForm({
                    username: cfg.username || "",
                    password: cfg.password || "",
                    enable_vlan: cfg.enable_vlan ?? false,
                    vlan_id: cfg.vlan_id || "",
                    port_binding: cfg.port_binding || {
                        lan1: true, lan2: true, lan3: true, lan4: true,
                    },
                    selected_ssids: cfg.selected_ssids || [],
                });
            }
        }
    }, [data]);

    // Handler PPPoE
    const handlePppoeChange = (field, value) => {
        setPppoeForm((prev) => ({ ...prev, [field]: value }));
    };

    const handlePortBindingChange = (port) => {
        setPppoeForm((prev) => ({
            ...prev,
            port_binding: {
                ...prev.port_binding,
                [port]: !prev.port_binding[port],
            },
        }));
    };

    const handleSsidToggle = (ssidIndex) => {
        setPppoeForm((prev) => {
            const exists = prev.selected_ssids.includes(ssidIndex);
            return {
                ...prev,
                selected_ssids: exists
                    ? prev.selected_ssids.filter((i) => i !== ssidIndex)
                    : [...prev.selected_ssids, ssidIndex],
            };
        });
    };

    // Handler Perubahan Input Wi-Fi
    const handleWifiChange = (index, field, value) => {
        setWifiForm((prev) =>
            prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item))
        );
    };

    // Toggle Password Visibility Per Item
    const togglePasswordVisibility = (index) => {
        setShowPassword((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    // HANDLER SUBMIT UPDATE WI-FI
    const handleSubmitUpdateWifi = async (e) => {
        e.preventDefault();
        setLoadingUpdateWifi(true);

        try {
            const payload = {
                wifi_networks: wifiForm,
            };
            await GenieAcsService.updateWifiNetwork({ data: payload, id: id });
            alert("Konfigurasi Wi-Fi berhasil diperbarui!");
            refreshData();
        } catch (err) {
            alert("Gagal menyimpan data Wi-Fi.");
            console.error(err);
        } finally {
            setLoadingUpdateWifi(false);
        }
    };

    // HANDLER SUBMIT UPDATE PPPOE
    const handleSubmitUpdatePppoe = async (e) => {
        e.preventDefault();
        setLoadingUpdatePppoe(true);
        try {
            // Merge fixed + editable fields sebelum kirim
            const payload = {
                ...PPPoE_FIXED,
                ...pppoeForm,
                // Resolve selected_ssids ke nama SSID untuk backend
                selected_ssid_names: pppoeForm.selected_ssids.map(
                    (idx) => wifiForm[idx]?.ssid || `SSID #${wifiForm[idx]?.index}`
                ),
            };
            await GenieAcsService.updatePppoeConfig({ data: payload, id: id });
            alert("Konfigurasi PPPoE/WAN berhasil diperbarui!");
            refreshData();
        } catch (err) {
            alert("Gagal menyimpan konfigurasi PPPoE.");
            console.error(err);
        } finally {
            setLoadingUpdatePppoe(false);
        }
    };

    // Mock History Logs
    const logs = [
        { type: "inform", message: "Device Inform received. 1 parameter changed.", time: "10 mins ago" },
        { type: "boot", message: "Device booted successfully. Warm start.", time: "4 days ago" },
        { type: "reboot", message: "Reboot command initiated by Admin.", time: "4 days ago" },
        { type: "inform", message: "Periodic Inform.", time: "5 days ago" },
    ];

    // Helper untuk warna Rx Power
    const getRxColor = (val) => {
        if (val > -10) return "text-red-400";
        if (val < -25) return "text-red-400";
        return "text-emerald-400";
    };

    return (
        <>
            <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
                {/* Top Navigation Bar */}
                <header className="h-16 bg-slate-900/80 backdrop-blur-md border-b border-slate-700/50 sticky top-0 z-30 flex items-center px-4 lg:px-6">
                    <button
                        onClick={() => setShowSidebar(true)}
                        className="lg:hidden mr-4 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
                    >
                        <span className="text-xl">☰</span>
                    </button>

                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium mr-4"
                    >
                        <FaArrowLeft />
                        <span className="hidden sm:inline">Back to List</span>
                    </button>

                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center border border-slate-700 text-cyan-500">
                            <FaMicrochip />
                        </div>
                        <div>
                            <h1 className="text-base md:text-lg font-bold text-slate-100 leading-tight">{id}</h1>
                            <p className="text-xs text-slate-500 font-mono">{data.ip_address}</p>
                        </div>
                    </div>

                    <div className="ml-auto flex items-center gap-2">
                        <button className="px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-700 transition-all">
                            <FaPenToSquare className="mr-1 inline" /> Edit
                        </button>
                        <button className="px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 hover:bg-amber-500/20 transition-all">
                            <FaPowerOff className="mr-1 inline" /> Reboot
                        </button>
                    </div>
                </header>

                {/* Page Content */}
                <div className="p-4 lg:p-6 mx-auto w-full space-y-6">

                    {/* Overview Stats Grid */}
                    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard
                            icon={<FaCircleInfo />}
                            label="Status"
                            value={data.status}
                            type="status"
                        />
                        <StatCard
                            icon={<FaNetworkWired />}
                            label="Model"
                            value={`${data?.device_info?.oui} ${data?.device_info?.product_class}`}
                        />
                        <StatCard
                            icon={<FaWifi />}
                            label="Firmware"
                            value={data?.device_info?.firmware}
                        />
                        <StatCard
                            icon={<FaHistory />}
                            label="Uptime"
                            value={data?.uptime}
                        />
                    </section>

                    {/* Main Details Area */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                        {/* Left Column: Details & Settings (2/3 width) */}
                        <div className="lg:col-span-2 space-y-6">

                            {/* 1. Device Info + Optical Signal */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Device Info Basic */}
                                <div className="rounded-2xl p-5 bg-slate-900/60 border border-slate-700/50 backdrop-blur-sm">
                                    <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
                                        <FaMicrochip className="text-cyan-500" /> Device Information
                                    </h3>
                                    <div className="space-y-3">
                                        <InfoRow label="Manufaktur" value={data?.device_info?.manufacturer} highlight />
                                        <InfoRow label="Model" value={data?.device_info?.product_class} highlight />
                                        <InfoRow label="Serial Number" value={data?.device_info?.serial_number} />
                                        <InfoRow label="IP Address" value={data.ip_address} highlight />
                                        <InfoRow label="MAC Address" value={data.mac_address} />
                                        <InfoRow label="Username" value={data?.pppoe?.username} />
                                        <InfoRow label="Password" value={data?.pppoe?.password} />
                                    </div>
                                </div>

                                {/* Optical Signal */}
                                <div className="rounded-2xl p-5 bg-slate-900/60 border border-slate-700/50 backdrop-blur-sm">
                                    <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
                                        <FaSignal className="text-cyan-500" /> Optical Signal
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-end">
                                            <span className="text-xs text-slate-500 uppercase">Rx Power (Redaman)</span>
                                            <span className={`text-lg font-bold font-mono ${getRxColor(data?.rx_power)}`}>
                                                {data?.rx_power} dBm
                                            </span>
                                        </div>
                                        <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-red-500 via-emerald-500 to-red-500 w-full opacity-50"
                                                style={{ clipPath: `inset(0 ${((data.rx_power + 35) / 30) * 100}% 0 0)` }}
                                            ></div>
                                        </div>
                                        <div className="flex justify-between items-end pt-2">
                                            <span className="text-xs text-slate-500 uppercase">Tx Power</span>
                                            <span className="text-lg font-bold font-mono text-slate-200">
                                                {data?.tx_power} dBm
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-end pt-2">
                                            <span className="text-xs text-slate-500 uppercase">Temperature</span>
                                            <span className="text-sm font-mono text-slate-300">
                                                {data?.temp}°C
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 2. Connected Hosts Table */}
                            <div className="rounded-2xl bg-slate-900/60 border border-slate-700/50 backdrop-blur-sm overflow-hidden">
                                <div className="p-5 border-b border-slate-700/50 flex justify-between items-center">
                                    <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                                        <FaNetworkWired className="text-cyan-500" /> Connected Hosts
                                    </h3>
                                    <span className="text-xs bg-slate-800 text-slate-400 px-2 py-1 rounded">
                                        {data?.connected_device?.length} Active
                                    </span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-slate-800/50 text-xs text-slate-400 uppercase">
                                            <tr>
                                                <th className="px-5 py-3 font-medium">Device Name</th>
                                                <th className="px-5 py-3 font-medium">IP Address</th>
                                                <th className="px-5 py-3 font-medium">MAC Address</th>
                                                <th className="px-5 py-3 font-medium text-right">Type</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-700/30">
                                            {data && data?.connected_devices && data?.connected_devices?.map((host, idx) => (
                                                <tr key={idx} className="hover:bg-slate-800/30 transition-colors">
                                                    <td className="px-5 py-3 text-slate-300 text-xs flex items-center gap-2">
                                                        {host.type === 'wifi' ? <FaMobileScreen className="text-slate-500" /> : <FaLaptop className="text-slate-500" />}
                                                        {host.hostname}
                                                    </td>
                                                    <td className="px-5 py-3 text-slate-400 text-xs font-mono">{host.ip_address}</td>
                                                    <td className="px-5 py-3 text-slate-500 text-xs font-mono">{host.mac_address}</td>
                                                    <td className="px-5 py-3 text-right">
                                                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${host.type === 'wifi' ? 'bg-blue-500/10 text-blue-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                                                            {host.type}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* ========================================== */}
                            {/* 3. PPPOE / WAN CONFIGURATION (SIMPLIFIED)  */}
                            {/* ========================================== */}
                            <form onSubmit={handleSubmitUpdatePppoe} className="rounded-2xl bg-slate-900/60 border border-slate-700/50 backdrop-blur-sm overflow-hidden">
                                {/* Header */}
                                <div className="p-5 pb-4 border-b border-slate-700/40">
                                    <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                                        <FaGlobe className="text-cyan-500" /> WAN / PPPoE Configuration
                                    </h3>
                                    <p className="text-[11px] text-slate-500 mt-1">Konfigurasi dasar WAN sudah di-set otomatis. Sesuaikan field di bawah sesuai kebutuhan.</p>
                                </div>

                                <div className="p-5 space-y-5">

                                    {/* Fixed Config Chips — Read Only Info */}
                                    <div>
                                        <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-2 block">
                                            <FaLock className="inline mr-1 text-slate-600" /> Default Configuration (Fixed)
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            <ConfigChip label="IP" value={PPPoE_FIXED.ip_version} />
                                            <ConfigChip label="Mode" value={PPPoE_FIXED.mode} />
                                            <ConfigChip label="Name" value={PPPoE_FIXED.connection_name} />
                                            <ConfigChip label="DHCP" value={PPPoE_FIXED.dhcp_enable ? "ON" : "OFF"} />
                                            <ConfigChip label="NAT" value={PPPoE_FIXED.nat_enable ? "ON" : "OFF"} />
                                            <ConfigChip label="Service" value={PPPoE_FIXED.business_model} />
                                            <ConfigChip label="VLAN Mode" value={PPPoE_FIXED.vlan_mode} />
                                            <ConfigChip label="802.1p" value={PPPoE_FIXED.p_802_1p} />
                                            <ConfigChip label="MTU" value={PPPoE_FIXED.mtu} />
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="h-px bg-slate-700/40" />

                                    {/* Editable Fields Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                                        {/* Kiri: Autentikasi + VLAN */}
                                        <div className="space-y-4">
                                            {/* PPPoE Username */}
                                            <div>
                                                <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1.5 block">
                                                    PPPoE Username
                                                </label>
                                                <input
                                                    type="text"
                                                    value={pppoeForm.username}
                                                    onChange={(e) => handlePppoeChange('username', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-cyan-500/50 transition-colors"
                                                    placeholder="user@isp.net"
                                                />
                                            </div>

                                            {/* PPPoE Password */}
                                            <div>
                                                <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1.5 block">
                                                    PPPoE Password
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={showPassword['pppoe'] ? "text" : "password"}
                                                        value={pppoeForm.password}
                                                        onChange={(e) => handlePppoeChange('password', e.target.value)}
                                                        className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:border-cyan-500/50 transition-colors"
                                                        placeholder="••••••••"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePasswordVisibility('pppoe')}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                                                    >
                                                        {showPassword['pppoe'] ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Enable VLAN Toggle */}
                                            <div className="flex items-center justify-between bg-slate-800/40 rounded-xl px-4 py-3 border border-slate-700/30">
                                                <div>
                                                    <span className="text-xs text-slate-300 font-medium">Enable VLAN</span>
                                                    <p className="text-[10px] text-slate-500 mt-0.5">Aktifkan VLAN tagging pada koneksi ini</p>
                                                </div>
                                                <label className="relative h-7 w-12 flex items-center px-1 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer flex-shrink-0">
                                                    <input
                                                        type="checkbox"
                                                        checked={pppoeForm.enable_vlan}
                                                        onChange={(e) => handlePppoeChange('enable_vlan', e.target.checked)}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-5 h-5 bg-slate-600 peer-checked:bg-cyan-500 rounded-full transition-all peer-checked:translate-x-5"></div>
                                                </label>
                                            </div>

                                            {/* VLAN ID (conditional) */}
                                            {pppoeForm.enable_vlan && (
                                                <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                                                    <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1.5 block">
                                                        VLAN ID
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={pppoeForm.vlan_id}
                                                        onChange={(e) => handlePppoeChange('vlan_id', e.target.value)}
                                                        className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-cyan-500/50 transition-colors"
                                                        placeholder="100"
                                                        min="1"
                                                        max="4094"
                                                    />
                                                    <p className="text-[10px] text-slate-500 mt-1">Range: 1 – 4094</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Kanan: Port Binding + SSID Selection */}
                                        <div className="space-y-4">
                                            {/* Port Binding */}
                                            <div className="bg-slate-800/30 p-4 rounded-xl border border-slate-700/30">
                                                <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-3 flex items-center gap-1.5">
                                                    <FaNetworkWired className="text-slate-600" size={10} />
                                                    Port Binding (LAN)
                                                </label>
                                                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                                                    <CheckboxSmall
                                                        label="LAN 1"
                                                        checked={pppoeForm.port_binding.lan1}
                                                        onChange={() => handlePortBindingChange('lan1')}
                                                    />
                                                    <CheckboxSmall
                                                        label="LAN 2"
                                                        checked={pppoeForm.port_binding.lan2}
                                                        onChange={() => handlePortBindingChange('lan2')}
                                                    />
                                                    <CheckboxSmall
                                                        label="LAN 3"
                                                        checked={pppoeForm.port_binding.lan3}
                                                        onChange={() => handlePortBindingChange('lan3')}
                                                    />
                                                    <CheckboxSmall
                                                        label="LAN 4"
                                                        checked={pppoeForm.port_binding.lan4}
                                                        onChange={() => handlePortBindingChange('lan4')}
                                                    />
                                                </div>
                                            </div>

                                            {/* SSID Selection (Dynamic dari wifiForm) */}
                                            <SsidMultiSelect
                                                wifiForm={wifiForm}
                                                selectedSsids={pppoeForm.selected_ssids}
                                                onToggle={handleSsidToggle}
                                                disabled={false}
                                            />
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="flex justify-end pt-1">
                                        <button
                                            type="submit"
                                            disabled={loadingUpdatePppoe}
                                            className="text-xs bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:text-slate-500 text-white px-6 py-2.5 rounded-lg transition-all shadow-lg shadow-cyan-900/20 font-medium"
                                        >
                                            {loadingUpdatePppoe ? (
                                                <span className="flex items-center gap-2">
                                                    <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                    </svg>
                                                    Saving...
                                                </span>
                                            ) : (
                                                "Save PPPoE Config"
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>

                            {/* 4. WI-FI CONFIGURATION FORM */}
                            <form onSubmit={handleSubmitUpdateWifi} className="rounded-2xl p-5 bg-slate-900/60 border border-slate-700/50 backdrop-blur-sm space-y-4">
                                <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                                    <FaWifi className="text-cyan-500" /> Wi-Fi Configuration
                                </h3>

                                {wifiForm.map((wifi, idx) => (
                                    <div key={idx} className="p-3 bg-slate-800/40 rounded-xl border border-slate-700/30">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="flex items-center gap-3">
                                                <div>
                                                    <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1.5 block">
                                                        SSID #{wifi.index}
                                                    </label>
                                                    <label className="relative h-10 w-14 flex items-center px-1.5 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={wifi.enable}
                                                            onChange={(e) => handleWifiChange(idx, "enable", e.target.checked)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-7 h-7 bg-slate-600 peer-checked:bg-cyan-500 rounded-full transition-all peer-checked:translate-x-3.5"></div>
                                                    </label>
                                                </div>
                                                <div className="w-full">
                                                    <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1.5 block">SSID</label>
                                                    <input
                                                        type="text"
                                                        value={wifi.ssid}
                                                        onChange={(e) => handleWifiChange(idx, "ssid", e.target.value)}
                                                        className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-cyan-500/50 transition-all"
                                                        placeholder="SSID Name"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1.5 block">Password</label>
                                                <div className="relative">
                                                    <input
                                                        type={showPassword[idx] ? "text" : "password"}
                                                        value={wifi.password}
                                                        onChange={(e) => handleWifiChange(idx, "password", e.target.value)}
                                                        className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:border-cyan-500/50 transition-all"
                                                        placeholder="WPA Password"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePasswordVisibility(idx)}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                                                    >
                                                        {showPassword[idx] ? <FaEyeSlash /> : <FaEye />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <div className="mt-4 flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={loadingUpdateWifi}
                                        className="text-xs bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 text-white px-5 py-2.5 rounded-lg transition-colors shadow-lg shadow-cyan-900/20 font-medium"
                                    >
                                        {loadingUpdateWifi ? "Saving..." : "Save Changes"}
                                    </button>
                                </div>
                            </form>

                        </div>

                        {/* Right Column: Tags & History */}
                        <div className="space-y-6">
                            {/* Tags */}
                            <div className="rounded-2xl p-5 bg-slate-900/60 border border-slate-700/50 backdrop-blur-sm">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                                        <FaTag className="text-cyan-500" /> Tags
                                    </h3>
                                    <button
                                        type="button"
                                        className="text-xs text-cyan-400 hover:text-cyan-300"
                                        onClick={() => setShowAddTagModal(true)}
                                    >
                                        + Add
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {data && data?.tags && data?.tags.map((tag, i) => (
                                        <span key={i} className="px-2.5 py-1 rounded-md bg-slate-800 border border-slate-700 text-xs text-slate-300">
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* History Logs */}
                            <div className="rounded-2xl p-5 bg-slate-900/60 border border-slate-700/50 backdrop-blur-sm">
                                <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
                                    <FaHistory className="text-cyan-500" /> Event History
                                </h3>
                                <div className="space-y-0">
                                    {logs.map((log, i) => (
                                        <LogItem key={i} {...log} />
                                    ))}
                                </div>
                                <button className="w-full mt-4 text-xs text-center text-slate-500 hover:text-slate-300 py-2 border border-dashed border-slate-700 rounded-lg hover:border-slate-500 transition-colors">
                                    View Full Logs
                                </button>
                            </div>

                            {/* Danger Zone */}
                            <div className="rounded-2xl p-5 bg-red-500/5 border border-red-500/20 backdrop-blur-sm">
                                <h3 className="text-sm font-semibold text-red-400 mb-2">Danger Zone</h3>
                                <p className="text-xs text-slate-400 mb-4">
                                    Performing a factory reset will erase all custom configurations on the device.
                                </p>
                                <button className="w-full py-2 rounded-lg border border-red-500/30 text-red-400 text-xs font-medium hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2">
                                    <FaTrashCan /> Factory Reset
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </DashboardLayout>

            {/* Modal Add Tag */}
            <GenieAcsAddDeviceTag
                deviceId={id}
                currentTags={data?.tags || []}
                openModal={showAddTagModal}
                setOpenModal={setShowAddTagModal}
                refresh={refreshData}
            />
        </>
    );
};

// ============================================
// HELPER COMPONENTS
// ============================================

const StatCard = ({ icon, label, value, type }) => {
    const getStatusColor = (status) => {
        if (status === 'online') return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
        return 'text-red-400 bg-red-500/10 border-red-500/20';
    };

    return (
        <div className="rounded-2xl p-4 bg-slate-900/60 border border-slate-700/50 backdrop-blur-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl ${type === 'status' ? getStatusColor(value) : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'}`}>
                <div className="text-lg">{icon}</div>
            </div>
            <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{label}</p>
                <p className={`text-sm font-bold ${type === 'status' && value === 'online' ? 'text-emerald-400' : 'text-slate-200'}`}>
                    {value}
                </p>
            </div>
        </div>
    );
};

const InfoRow = ({ label, value, highlight = false }) => (
    <div className="flex flex-col">
        <span className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide">{label}</span>
        <span className={`text-sm font-mono mt-0.5 ${highlight ? 'text-cyan-400 font-bold' : 'text-slate-300'}`}>{value}</span>
    </div>
);